<template>
  <div class="home">
    <!-- ============ HERO ============ -->
    <section class="wt-hero">
      <div class="wt-container wt-hero__grid">
        <div class="wt-hero__text">
          <span class="wt-eyebrow">Waizmann<span class="wt-eyebrow__suffix">.089</span> · die ZZV-Sprechstunde</span>
          <span class="wt-60chip">Speziell für Menschen 60Plus</span>
          <h1 class="wt-hero__h">
            Die <span class="wt-hero__h-accent">Zahnzusatz</span>-Sprechstunde
            für 60Plus — telefonisch &amp; persönlich.
          </h1>
          <p class="wt-hero__sub">
            Sie wollen die richtige Zahnzusatzversicherung — <strong>ohne selbst
            zu vergleichen, ohne Stress</strong>? In 10 bis 15 Minuten am Telefon
            bekommen Sie eine <strong>klare Tarif-Empfehlung</strong> auf Basis
            der WaizmannTabelle<sup>®</sup>. Kostenlos und unverbindlich.
          </p>

          <ul class="wt-hero__meta">
            <li><span class="wt-check">✔</span> 100 % kostenlos</li>
            <li><span class="wt-check">✔</span> Keine Vorbereitung nötig</li>
            <li><span class="wt-check">✔</span> Sie entscheiden in Ruhe</li>
          </ul>
        </div>

        <div class="wt-hero__visual">
          <div class="wt-portrait">
            <img
              :src="kili2Img"
              alt="Kilian Link — Ihr Berater für die telefonische Zahnzusatz-Sprechstunde"
              class="wt-portrait__img"
            />
            <div class="wt-portrait__sticker">
              <span class="wt-portrait__phone">📞</span>
              Kilian Link · Ihr Berater
            </div>
          </div>
          <aside class="wt-original wt-original--mini" aria-label="Das Original seit 1995">
            <span class="wt-original__big">Das Original</span>
            <span class="wt-original__no">seit 1995</span>
          </aside>
        </div>
      </div>
    </section>

    <!-- ============ KALENDER ============ -->
    <section id="kalender" class="wt-section wt-section--soft">
      <div class="wt-container">
        <div class="wt-pager">
          <button
            class="wt-pager__btn"
            :disabled="pageIndex === 0"
            @click="changePage(-1)"
          >
            <span aria-hidden="true">←</span> Vorherige
          </button>

          <div class="wt-pager__range" v-if="range">
            <span class="wt-pager__label">Zeitraum</span>
            <span class="wt-pager__date">{{ formatRange(range.from, range.to) }}</span>
          </div>

          <button class="wt-pager__btn" @click="changePage(1)">
            Nächste <span aria-hidden="true">→</span>
          </button>
        </div>

        <div v-if="loading" class="wt-loading-inline">
          <div class="wt-loading__spinner"></div>
          <span>Lade Termine…</span>
        </div>

        <div v-if="!loading && !hasSlots" class="wt-empty-banner">
          <span>
            {{ emptyText || "In diesem Zeitraum sind aktuell keine Termine verfügbar. Bitte wählen Sie den nächsten Zeitraum." }}
          </span>
        </div>

        <div class="wt-legend">
          <span class="wt-legend__item">
            <span class="wt-dot wt-dot--free"></span> frei — direkt buchbar
          </span>
          <span class="wt-legend__item">
            <span class="wt-dot wt-dot--booked"></span> bereits vergeben
          </span>
        </div>

        <div class="wt-grid" :class="{ 'wt-grid--loading': loading }">
          <div
            v-for="day in days"
            :key="day.date"
            class="wt-day"
            :class="{
              'wt-day--empty':    !day.slots || !day.slots.length,
              'wt-day--weekend':  isWeekend(day.date),
              'wt-day--today':    isToday(day.date),
              'wt-day--full':     day.slots && day.slots.length && !freeCount(day),
            }"
          >
            <div class="wt-day__head">
              <div class="wt-day__datebox">
                <span class="wt-day__num">{{ dayNum(day.date) }}</span>
                <span class="wt-day__meta">
                  <span class="wt-day__weekday">{{ weekdayLabel(day.date) }}</span>
                  <span class="wt-day__month">{{ monthShort(day.date) }}</span>
                </span>
              </div>
              <div class="wt-day__badges">
                <span v-if="isToday(day.date)" class="wt-day__badge">heute</span>
                <span
                  v-if="day.slots && day.slots.length"
                  class="wt-day__avail"
                  :class="{ 'wt-day__avail--full': !freeCount(day) }"
                >
                  <template v-if="freeCount(day)">{{ freeCount(day) }} frei</template>
                  <template v-else>ausgebucht</template>
                </span>
              </div>
            </div>

            <div v-if="day.slots && day.slots.length" class="wt-day__slots">
              <button
                v-for="slot in day.slots"
                :key="slot.slot_id"
                class="wt-slot"
                :class="{
                  'wt-slot--booked': slot.is_booked,
                  'wt-slot--free':  !slot.is_booked,
                }"
                :disabled="slot.is_booked"
                @click="openBooking(slot)"
              >
                <span class="wt-slot__time">{{ slot.time }}</span>
                <span class="wt-slot__state">
                  {{ slot.is_booked ? "belegt" : "frei" }}
                </span>
              </button>
            </div>

            <div v-else class="wt-day__noslots">
              <span>—</span>
              <small>keine Termine</small>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ PROBLEME ============ -->
    <section class="wt-section wt-section--blue">
      <div class="wt-container">
        <div class="wt-sechead">
          <span class="wt-eyebrow">Vielleicht kennen Sie das</span>
          <h2 class="wt-h2">Kommt Ihnen das bekannt vor?</h2>
        </div>
        <div class="wt-problems">
          <div class="wt-problem">
            <span class="wt-problem__x">✕</span>
            <h3>Zu viele Zahn-Tarife</h3>
            <p>
              Hunderte Angebote, unzählige Bausteine — und nach drei
              Vergleichsrechnern sind Sie verwirrter als vorher.
            </p>
          </div>
          <div class="wt-problem">
            <span class="wt-problem__x">✕</span>
            <h3>Keine klare Entscheidung</h3>
            <p>
              Sie wollen handeln, aber irgendetwas fehlt — eine ehrliche
              Empfehlung, die Ihre Situation wirklich versteht.
            </p>
          </div>
          <div class="wt-problem">
            <span class="wt-problem__x">✕</span>
            <h3>Angst, das Falsche abzuschließen</h3>
            <p>
              Kleingedrucktes, Wartezeiten, Begrenzungen — gerade ab 60 wollen
              Sie nichts falsch machen.
            </p>
          </div>
        </div>
        <p class="wt-problems__bridge">
          Genau dafür gibt es die <strong>ZZV-Sprechstunde</strong>: Sie bekommen
          eine <strong>Entscheidung</strong> — keine Auswahl.
        </p>
      </div>
    </section>

    <!-- ============ ABLAUF ============ -->
    <section class="wt-section">
      <div class="wt-container">
        <div class="wt-sechead">
          <span class="wt-eyebrow">Ihre Sprechstunde</span>
          <h2 class="wt-h2">So einfach geht's</h2>
        </div>
        <ol class="wt-flow">
          <li class="wt-flowstep">
            <span class="wt-flowstep__icon">📅</span>
            <span class="wt-flowstep__num">1</span>
            <h3>Termin buchen</h3>
            <p>Oben im Kalender in wenigen Klicks den Wunschtermin auswählen.</p>
          </li>
          <li class="wt-flowstep">
            <span class="wt-flowstep__icon">📞</span>
            <span class="wt-flowstep__num">2</span>
            <h3>Telefonberatung</h3>
            <p>Wir telefonieren in Ruhe — 10 bis 15 Minuten, ganz unverbindlich.</p>
          </li>
          <li class="wt-flowstep">
            <span class="wt-flowstep__icon">🎯</span>
            <span class="wt-flowstep__num">3</span>
            <h3>Klare Empfehlung</h3>
            <p>Sie erhalten <strong>genau einen Tarif</strong>, der zu Ihrer Situation passt — verständlich begründet.</p>
          </li>
          <li class="wt-flowstep">
            <span class="wt-flowstep__icon">✉️</span>
            <span class="wt-flowstep__num">4</span>
            <h3>Unterlagen erhalten</h3>
            <p><strong>Unterschriftsreife Unterlagen</strong> per Post oder E-Mail. Sie entscheiden in Ruhe.</p>
          </li>
        </ol>
      </div>
    </section>

    <!-- ============ ÜBER / BERATER ============ -->
    <section class="wt-section wt-section--blue">
      <div class="wt-container wt-about">
        <div class="wt-about__visual">
          <div class="wt-portrait wt-portrait--about">
            <img
              :src="kili1Img"
              alt="Kilian Link — Spezialist für Zahnzusatzversicherungen"
              class="wt-portrait__img"
            />
            <div class="wt-portrait__sticker">Kilian Link · München</div>
          </div>
        </div>
        <div class="wt-about__copy">
          <span class="wt-eyebrow">Ihr Ansprechpartner</span>
          <h2 class="wt-h2">Hallo, ich bin Kilian.</h2>
          <p>
            Seit über <strong>15 Jahren</strong> beschäftige ich mich
            ausschließlich mit Zahnzusatzversicherungen. Ich berate Menschen,
            die <strong>keine Lust auf komplizierte Vergleichsrechner</strong>
            haben, sondern eine verständliche, persönliche Empfehlung möchten —
            besonders gern die Generation <strong>60Plus</strong>.
          </p>
          <p>
            Ich analysiere Ihre Situation am Telefon und empfehle Ihnen genau
            den Tarif, der passt. Ohne Umwege. Ohne Verwirrung.
          </p>
          <ul class="wt-about__creds">
            <li><span class="wt-check">✔</span> Spezialist für Zahnzusatzversicherungen</li>
            <li><span class="wt-check">✔</span> Über 15 Jahre Erfahrung in der Tarif-Analyse</li>
            <li><span class="wt-check">✔</span> Geprüfter Versicherungsvermittler IHK (§ 34d GewO)</li>
          </ul>
        </div>
      </div>
    </section>

    <!-- ============ VERTRAUEN ============ -->
    <section class="wt-section">
      <div class="wt-container wt-trust">
        <div class="wt-trust__num">11.000+</div>
        <div class="wt-trust__copy">
          <h2 class="wt-h2">Die Nr. 1 bei Zahnärzten als Basis</h2>
          <p>
            Jede Empfehlung basiert auf den Analysen der
            <strong>WaizmannTabelle<sup>®</sup></strong> — über
            <strong>11.000 Zahnärzte</strong> in Deutschland empfehlen sie
            bereits. Das Original, seit 1995.
          </p>
        </div>
      </div>
    </section>

    <!-- ============ FINAL CTA ============ -->
    <section class="wt-finalcta">
      <div class="wt-container wt-finalcta__inner">
        <span class="wt-eyebrow wt-eyebrow--light">Nächster Schritt</span>
        <h2>Jetzt Klarheit schaffen.</h2>
        <p>In 10–15 Minuten zur richtigen Entscheidung — telefonisch und persönlich.</p>
        <a href="#kalender" class="wt-finalcta__btn">Termin jetzt sichern →</a>
        <p class="wt-finalcta__meta">100 % kostenlos · keine Vorbereitung nötig · Sie entscheiden in Ruhe</p>
      </div>
    </section>

    <!-- ============ MODAL ============ -->
    <div v-if="bookingSlot" class="wt-modal" @click.self="closeBooking">
      <div class="wt-modal__inner">
        <button class="wt-modal__close" aria-label="Schließen" @click="closeBooking">×</button>

        <span class="wt-eyebrow">Ihr Termin</span>
        <h2 class="wt-modal__h">Termin buchen</h2>
        <p class="wt-modal__when">
          {{ formatDateLong(bookingSlot.date) }} um
          <strong>{{ bookingSlot.time }} Uhr</strong>
          <span class="wt-modal__dur">({{ bookingSlot.duration }} Min.)</span>
        </p>

        <form v-if="!submitted" class="wt-form" @submit.prevent="submitBooking" novalidate>
          <label class="wt-form__field">
            <span>Name *</span>
            <input v-model="form.name" type="text" required autocomplete="name" />
          </label>

          <div class="wt-form__row">
            <label class="wt-form__field">
              <span>E-Mail *</span>
              <input v-model="form.email" type="email" required autocomplete="email" />
            </label>
            <label class="wt-form__field">
              <span>Telefon *</span>
              <input v-model="form.phone" type="tel" required autocomplete="tel" />
            </label>
          </div>

          <div class="wt-form__row">
            <label class="wt-form__field">
              <span>Geburtsdatum *</span>
              <input
                v-model="form.birthdate"
                type="text"
                inputmode="numeric"
                placeholder="z.B. 01.03.1961"
                autocomplete="bday"
              />
            </label>
            <label class="wt-form__field">
              <span>Geschlecht *</span>
              <select v-model="form.gender">
                <option value="" disabled>Bitte wählen…</option>
                <option value="weiblich">weiblich</option>
                <option value="männlich">männlich</option>
                <option value="divers">divers</option>
              </select>
            </label>
          </div>

          <fieldset class="wt-form__fieldset">
            <legend>Ihr Versicherungsstatus *</legend>
            <label
              v-for="opt in insuranceOptions"
              :key="opt.value"
              class="wt-form__radio"
            >
              <input
                type="radio"
                name="insurance"
                :value="opt.value"
                v-model="form.insurance"
              />
              <span>{{ opt.label }}</span>
            </label>
          </fieldset>

          <label class="wt-form__field">
            <span>Nachricht (optional)</span>
            <textarea
              v-model="form.message"
              rows="3"
              placeholder="Gibt es etwas, das ich vorab wissen sollte?"
            ></textarea>
          </label>

          <div class="wt-erstinfo" :class="{ 'wt-erstinfo--error': erstInfoError }">
            <label class="wt-erstinfo__check">
              <input
                v-model="erstInfoOk"
                type="checkbox"
                @change="erstInfoError = false"
              />
              <span class="wt-erstinfo__text">
                <strong><span class="wt-erstinfo__ja">Ja,</span> ich habe die
                Erstinformation für Kunden erhalten.</strong>
                Erstinformationen für Versicherungsmakler gemäß § 15 VersVermV
                <a class="wt-erstinfo__link" @click.prevent="showErstInfo = true">
                  jetzt anzeigen oder herunterladen</a>.
              </span>
            </label>
            <p v-if="erstInfoError" class="wt-erstinfo__hint">
              Bitte setzen Sie den Haken auf „Ja", um den Termin zu buchen.
            </p>
          </div>

          <div class="wt-prinzip">
            <p class="wt-prinzip__h">
              Wichtig: Versicherungsprinzip – bereits eingetretene Defekte können
              nicht mehr versichert werden.
            </p>
            <p>
              <strong>Was bedeutet das genau?</strong> Es kann keine
              Zahnzusatzversicherung mehr für bereits eingetretene Defekte
              abgeschlossen werden, um genau dafür eine Erstattung / Leistung zu
              erhalten.
            </p>
            <p>
              Bereits eingetretene Defekte, angeratene Behandlungen / angeratene
              Maßnahmen / geplante Behandlungen / Behandlungen, die bereits am
              Laufen sind, können nicht mehr versichert werden.
            </p>
            <p class="wt-prinzip__bsp">
              Z.B.: Meine Zahnkrone ist defekt. Ich habe eine Zahnlücke und der
              Zahnarzt hat mir dafür einen Heil- und Kostenplan erstellt.
            </p>
          </div>

          <div v-if="submitError" class="wt-form__error">{{ submitError }}</div>

          <button class="wt-form__cta" type="submit" :disabled="submitting">
            <span v-if="submitting">Wird gesendet…</span>
            <span v-else>Termin verbindlich buchen →</span>
          </button>

          <p class="wt-form__hint">
            Mit dem Buchen stimmen Sie der Verarbeitung Ihrer Daten zur
            Terminabwicklung zu.
          </p>
        </form>

        <div v-else class="wt-form__success">
          <h3>Vielen Dank!</h3>
          <p>Ihre Buchung wurde gespeichert.</p>
          <p>Eine Bestätigungsmail ist unterwegs.</p>
          <button class="wt-form__cta wt-form__cta--ghost" @click="closeBooking">
            Schließen
          </button>
        </div>
      </div>
    </div>

    <ErstInfoPop
      :open="showErstInfo"
      @close="showErstInfo = false"
      @update:open="showErstInfo = $event"
    />
  </div>
</template>

<script>
import BaseCalls from "@/services/BaseCalls.js";
import ErstInfoPop from "@/components/ui/ErstInfoPop.vue";
import kili1Img from "@/assets/kili1.jpg";
import kili2Img from "@/assets/kili2.jpg";

export default {
  name: "HomeIndex",
  components: { ErstInfoPop },
  data() {
    return {
      kili1Img,
      kili2Img,
      pageIndex: 0,
      days: [],
      range: null,
      hasSlots: false,
      intro: "",
      emptyText: "",
      loading: false,

      bookingSlot: null,
      form: { name: "", email: "", phone: "", birthdate: "", gender: "", insurance: "", message: "" },
      insuranceOptions: [
        { value: "gesetzliche_krankenkasse",          label: "Gesetzliche Krankenkasse" },
        { value: "heilfuersorge",                     label: "Heilfürsorge" },
        { value: "gesetzliche_krankenkasse_beihilfe", label: "Gesetzliche Krankenkasse und Beihilfe" },
        { value: "freie_heilfuersorge_beihilfe",      label: "Freie Heilfürsorge & Beihilfe" },
      ],
      submitting: false,
      submitted: false,
      submitError: "",
      erstInfoOk: false,
      erstInfoError: false,
      showErstInfo: false,
    };
  },
  mounted() {
    this.applyFallback();
    this.loadPage();
  },
  methods: {
    async loadPage() {
      this.loading = true;
      try {
        const r = await BaseCalls.terminGet({ action: "page", id: this.pageIndex });
        const page = r?.data?.page;
        if (page && Array.isArray(page.days) && page.days.length > 0) {
          this.days = page.days;
          this.range = page.range || this.buildFallbackRange(this.pageIndex);
          this.hasSlots = !!page.has_slots;
          this.intro = page.texts?.intro || "";
          this.emptyText = page.texts?.empty || "";
        } else {
          this.applyFallback();
          if (page) {
            this.intro = page.texts?.intro || this.intro;
            this.emptyText = page.texts?.empty || this.emptyText;
          }
        }
      } catch (e) {
        this.applyFallback();
      } finally {
        this.loading = false;
      }
    },
    applyFallback() {
      this.days = this.buildFallbackDays(this.pageIndex);
      this.range = this.buildFallbackRange(this.pageIndex);
      this.hasSlots = false;
    },
    buildFallbackDays(pageIndex) {
      const daysPerPage = 14;
      const start = this.startOfToday();
      start.setDate(start.getDate() + pageIndex * daysPerPage);
      const out = [];
      for (let i = 0; i < daysPerPage; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        out.push({
          date: this.toIsoDate(d),
          weekday: String(((d.getDay() + 6) % 7) + 1),
          slots: [],
        });
      }
      return out;
    },
    buildFallbackRange(pageIndex) {
      const daysPerPage = 14;
      const start = this.startOfToday();
      start.setDate(start.getDate() + pageIndex * daysPerPage);
      const end = new Date(start);
      end.setDate(start.getDate() + daysPerPage - 1);
      return {
        from: this.toIsoDate(start),
        to: this.toIsoDate(end),
      };
    },
    startOfToday() {
      const t = new Date();
      t.setHours(0, 0, 0, 0);
      return t;
    },
    toIsoDate(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    changePage(delta) {
      const next = Math.max(0, this.pageIndex + delta);
      if (next === this.pageIndex) return;
      this.pageIndex = next;
      this.applyFallback();
      this.loadPage();
    },
    openBooking(slot) {
      if (slot.is_booked) return;
      this.bookingSlot = slot;
      this.submitted = false;
      this.submitError = "";
      this.erstInfoOk = false;
      this.erstInfoError = false;
      this.form = { name: "", email: "", phone: "", birthdate: "", gender: "", insurance: "", message: "" };
    },
    closeBooking() {
      this.bookingSlot = null;
      if (this.submitted) this.loadPage();
    },
    async submitBooking() {
      if (!this.bookingSlot) return;
      if (!this.erstInfoOk) {
        this.erstInfoError = true;
        return;
      }
      if (!/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(this.form.birthdate.trim())) {
        this.submitError = "Bitte das Geburtsdatum im Format TT.MM.JJJJ angeben (z.B. 01.03.1961).";
        return;
      }
      if (!this.form.gender) {
        this.submitError = "Bitte das Geschlecht auswählen.";
        return;
      }
      if (!this.form.insurance) {
        this.submitError = "Bitte den Versicherungsstatus auswählen.";
        return;
      }
      this.submitting = true;
      this.submitError = "";
      try {
        const r = await BaseCalls.terminPost({
          action: "book",
          booking_slot_id: this.bookingSlot.slot_id,
          booking_name: this.form.name,
          booking_email: this.form.email,
          booking_phone: this.form.phone,
          booking_birthdate: this.form.birthdate.trim(),
          booking_gender: this.form.gender,
          booking_insurance: this.form.insurance,
          booking_message: this.form.message,
        });
        if (r?.data?.success) {
          this.submitted = true;
        } else {
          this.submitError = r?.data?.message || "Buchung fehlgeschlagen.";
        }
      } catch (e) {
        this.submitError = "Netzwerk-/Serverfehler.";
      } finally {
        this.submitting = false;
      }
    },
    formatDayShort(iso) {
      if (!iso) return "";
      const [, m, d] = iso.split("-");
      return `${d}.${m}.`;
    },
    formatDateLong(iso) {
      if (!iso) return "";
      const dt = new Date(iso + "T00:00:00");
      const wd = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"][dt.getDay()];
      const mn = ["Jan", "Feb", "März", "Apr", "Mai", "Juni", "Juli", "Aug", "Sep", "Okt", "Nov", "Dez"][dt.getMonth()];
      return `${wd}, ${dt.getDate()}. ${mn} ${dt.getFullYear()}`;
    },
    formatRange(a, b) {
      const fmt = (iso) => {
        const [y, m, d] = iso.split("-");
        return `${d}.${m}.${y}`;
      };
      return `${fmt(a)} – ${fmt(b)}`;
    },
    weekdayLabel(iso) {
      if (!iso) return "";
      const dt = new Date(iso + "T00:00:00");
      return ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][dt.getDay()];
    },
    dayNum(iso) {
      if (!iso) return "";
      return iso.split("-")[2];
    },
    monthShort(iso) {
      if (!iso) return "";
      const m = parseInt(iso.split("-")[1], 10);
      return ["Jan", "Feb", "März", "Apr", "Mai", "Juni", "Juli", "Aug", "Sep", "Okt", "Nov", "Dez"][m - 1] || "";
    },
    freeCount(day) {
      if (!day.slots || !day.slots.length) return 0;
      return day.slots.filter((s) => !s.is_booked).length;
    },
    isWeekend(iso) {
      if (!iso) return false;
      const dt = new Date(iso + "T00:00:00");
      const d = dt.getDay();
      return d === 0 || d === 6;
    },
    isToday(iso) {
      if (!iso) return false;
      const t = new Date();
      const today = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
      return iso === today;
    },
  },
};
</script>

<style scoped>
/* ============== HERO ============== */
.wt-hero {
  background: var(--wt-blue-soft);
  padding: 56px 0 40px;
  border-bottom: 1px solid var(--wt-blue-line);
}
.wt-hero__grid {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 48px;
  align-items: center;
}

/* ---- 60Plus-Chip ---- */
.wt-60chip {
  display: inline-block;
  background: var(--wt-yellow);
  color: var(--wt-ink);
  font-size: 12.5px;
  font-weight: 900;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  padding: 5px 14px;
  border-radius: 999px;
  margin: 0 0 14px;
}

/* ---- Hero Visual: Porträt + Original-Badge ---- */
.wt-hero__visual {
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: stretch;
}
.wt-portrait {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  border: 2px solid var(--wt-blue-line);
  box-shadow: 0 18px 44px rgba(11, 31, 59, 0.16);
  background: #fff;
}
.wt-portrait__img {
  display: block;
  width: 100%;
  aspect-ratio: 3 / 3.4;
  object-fit: cover;
  object-position: top;
}
.wt-portrait__sticker {
  position: absolute;
  left: 12px;
  bottom: 12px;
  background: #fff;
  color: var(--wt-ink);
  font-size: 13px;
  font-weight: 800;
  padding: 7px 14px;
  border-radius: 999px;
  box-shadow: 0 6px 16px rgba(11, 31, 59, 0.22);
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.wt-original--mini {
  flex-direction: row;
  align-items: baseline;
  justify-content: center;
  aspect-ratio: auto;
  width: 100%;
  padding: 12px 16px;
  gap: 10px;
}
.wt-original--mini .wt-original__big {
  font-size: 22px;
  letter-spacing: -0.6px;
}
.wt-original--mini .wt-original__no {
  font-size: 22px;
  letter-spacing: -0.6px;
  color: var(--wt-yellow);
}
.wt-hero__text {
  text-align: left;
}
@media (max-width: 860px) {
  .wt-hero__grid {
    grid-template-columns: 1fr;
    gap: 28px;
    justify-items: center;
  }
  .wt-hero__text { text-align: center; }
  .wt-hero__meta { justify-content: center; }
  .wt-hero__visual { max-width: 320px; }
}

/* ============== CONTENT-SEKTIONEN ============== */
.wt-section--blue {
  background: var(--wt-blue-soft);
  border-top: 1px solid var(--wt-blue-line);
  border-bottom: 1px solid var(--wt-blue-line);
}
.wt-sechead {
  text-align: center;
  margin-bottom: 32px;
}
.wt-h2 {
  font-size: clamp(26px, 3.6vw, 38px);
  font-weight: 900;
  letter-spacing: -0.6px;
  color: var(--wt-ink);
  margin: 4px 0 0;
  line-height: 1.12;
}

/* ---- Probleme ---- */
.wt-problems {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}
@media (max-width: 860px) {
  .wt-problems { grid-template-columns: 1fr; }
}
.wt-problem {
  background: #fff;
  border: 1.5px solid var(--wt-blue-line);
  border-radius: 14px;
  padding: 22px 20px;
}
.wt-problem__x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #fdecea;
  color: var(--wt-red);
  font-weight: 900;
  margin-bottom: 12px;
}
.wt-problem h3 {
  font-size: 17px;
  font-weight: 900;
  letter-spacing: -0.2px;
  color: var(--wt-ink);
  margin: 0 0 6px;
}
.wt-problem p {
  font-size: 14.5px;
  line-height: 1.65;
  color: var(--wt-text);
  margin: 0;
}
.wt-problems__bridge {
  text-align: center;
  font-size: 17px;
  color: var(--wt-ink);
  margin: 28px 0 0;
}
.wt-problems__bridge strong { color: var(--wt-blue); }

/* ---- Ablauf ---- */
.wt-flow {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
  counter-reset: step;
}
@media (max-width: 980px) {
  .wt-flow { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 540px) {
  .wt-flow { grid-template-columns: 1fr; }
}
.wt-flowstep {
  position: relative;
  background: #fff;
  border: 1.5px solid var(--wt-blue-line);
  border-radius: 14px;
  padding: 24px 18px 20px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.wt-flowstep:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 28px rgba(11, 31, 59, 0.12);
}
.wt-flowstep__icon {
  font-size: 30px;
  line-height: 1;
  display: block;
  margin-bottom: 10px;
}
.wt-flowstep__num {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--wt-blue);
  color: #fff;
  font-size: 14px;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.wt-flowstep h3 {
  font-size: 16.5px;
  font-weight: 900;
  color: var(--wt-ink);
  margin: 0 0 6px;
}
.wt-flowstep p {
  font-size: 14px;
  line-height: 1.6;
  color: var(--wt-text);
  margin: 0;
}

/* ---- Über / Berater ---- */
.wt-about {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 44px;
  align-items: center;
}
@media (max-width: 860px) {
  .wt-about { grid-template-columns: 1fr; justify-items: center; }
  .wt-about__visual { max-width: 300px; }
  .wt-about__copy { text-align: center; }
  .wt-about__creds { justify-items: center; }
}
.wt-portrait--about .wt-portrait__img {
  aspect-ratio: 3 / 3.6;
}
.wt-about__copy p {
  font-size: 15.5px;
  line-height: 1.7;
  color: var(--wt-text);
  margin: 0 0 14px;
  max-width: 560px;
}
.wt-about__copy .wt-h2 { margin-bottom: 12px; }
.wt-about__creds {
  list-style: none;
  padding: 0;
  margin: 6px 0 0;
  display: grid;
  gap: 8px;
  font-size: 14.5px;
  font-weight: 600;
  color: var(--wt-ink);
}

/* ---- Vertrauen ---- */
.wt-trust {
  display: flex;
  align-items: center;
  gap: 36px;
  background: var(--wt-blue);
  color: #fff;
  border-radius: 18px;
  padding: 34px 38px;
  flex-wrap: wrap;
}
.wt-trust__num {
  font-size: clamp(48px, 7vw, 76px);
  font-weight: 900;
  letter-spacing: -2px;
  line-height: 1;
  color: var(--wt-yellow);
  flex-shrink: 0;
}
.wt-trust__copy { flex: 1; min-width: 260px; }
.wt-trust__copy .wt-h2 { color: #fff; margin: 0 0 8px; }
.wt-trust__copy p {
  margin: 0;
  font-size: 15.5px;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.9);
}

/* ---- Final CTA ---- */
.wt-finalcta {
  background: linear-gradient(135deg, var(--wt-blue) 0%, var(--wt-blue-deep) 100%);
  padding: 64px 0 68px;
}
.wt-finalcta__inner {
  text-align: center;
  color: #fff;
}
.wt-eyebrow--light { color: var(--wt-yellow); }
.wt-finalcta h2 {
  font-size: clamp(30px, 4.5vw, 46px);
  font-weight: 900;
  letter-spacing: -0.8px;
  margin: 6px 0 10px;
}
.wt-finalcta p {
  font-size: 16.5px;
  color: rgba(255, 255, 255, 0.88);
  margin: 0 0 26px;
}
.wt-finalcta__btn {
  display: inline-block;
  background: var(--wt-yellow);
  color: var(--wt-ink) !important;
  font-size: 17px;
  font-weight: 900;
  letter-spacing: 0.3px;
  padding: 16px 34px;
  border-radius: 999px;
  text-decoration: none !important;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.28);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.wt-finalcta__btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.34);
}
.wt-finalcta__meta {
  margin: 18px 0 0 !important;
  font-size: 13.5px !important;
  color: rgba(255, 255, 255, 0.7) !important;
}
.wt-eyebrow {
  display: inline-block;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--wt-blue-deep);
  margin-bottom: 12px;
}
.wt-eyebrow__suffix {
  color: var(--wt-blue);
  font-weight: 900;
}
.wt-hero__h {
  font-size: clamp(34px, 5vw, 56px);
  font-weight: 900;
  color: var(--wt-ink);
  letter-spacing: -0.8px;
  line-height: 1.05;
  margin: 0 0 16px;
}
.wt-hero__h-accent {
  color: var(--wt-blue);
}
.wt-hero__sub {
  font-size: 17px;
  color: var(--wt-text);
  max-width: 640px;
  margin: 0 0 20px;
}
.wt-hero__meta {
  list-style: none;
  padding: 0;
  margin: 14px 0 0;
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  font-size: 14px;
  font-weight: 600;
  color: var(--wt-ink);
}
.wt-check {
  color: var(--wt-green);
  font-weight: 900;
  margin-right: 4px;
}

/* ============== SECTION ============== */
.wt-section {
  padding: 48px 0 56px;
}
.wt-section--soft {
  background: #fff;
}

/* ============== PAGER ============== */
.wt-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}
.wt-pager__btn {
  font-family: inherit;
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.3px;
  background: #fff;
  color: var(--wt-blue-deep);
  border: 2px solid var(--wt-blue-line);
  padding: 10px 20px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.wt-pager__btn:hover:not(:disabled) {
  border-color: var(--wt-blue);
  color: var(--wt-blue);
}
.wt-pager__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.wt-pager__range {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.wt-pager__label {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: var(--wt-muted);
  line-height: 1.4;
}
.wt-pager__date {
  font-size: 18px;
  font-weight: 800;
  color: var(--wt-ink);
}

/* ============== LEGENDE ============== */
.wt-legend {
  display: flex;
  align-items: center;
  gap: 22px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}
.wt-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: var(--wt-text);
}
.wt-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}
.wt-dot--free {
  background: var(--wt-green);
  box-shadow: 0 0 0 3px rgba(46, 160, 90, 0.18);
}
.wt-dot--booked {
  background: var(--wt-red);
  box-shadow: 0 0 0 3px rgba(200, 60, 50, 0.14);
}

/* ============== GRID ============== */
.wt-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}
@media (max-width: 1100px) {
  .wt-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 820px) {
  .wt-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 480px) {
  .wt-grid { grid-template-columns: 1fr; }
}

.wt-day {
  position: relative;
  background: #fff;
  border: 1.5px solid var(--wt-blue-line);
  border-radius: 16px;
  padding: 18px 16px 16px;
  min-height: 250px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    transform 0.18s ease;
}
.wt-day::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(90deg, var(--wt-blue), var(--wt-blue-deep));
  opacity: 0;
  transition: opacity 0.18s ease;
}
.wt-day:hover {
  border-color: var(--wt-blue);
  box-shadow: 0 14px 34px rgba(20, 50, 100, 0.16);
  transform: translateY(-4px);
}
.wt-day:hover::before {
  opacity: 1;
}
.wt-day--weekend {
  background: linear-gradient(180deg, #f5f8fc 0%, #fff 70%);
}
.wt-day--empty {
  background: #fafbfd;
  border-style: dashed;
  min-height: 180px;
}
.wt-day--empty:hover {
  box-shadow: none;
  transform: none;
  border-color: var(--wt-blue-line);
}
.wt-day--empty:hover::before {
  opacity: 0;
}
.wt-day--today {
  border-color: var(--wt-blue);
  border-width: 2px;
  box-shadow: 0 8px 24px rgba(44, 91, 158, 0.18);
}
.wt-day--today::before {
  opacity: 1;
  background: linear-gradient(90deg, var(--wt-yellow), var(--wt-blue));
}
.wt-day--full .wt-day__num {
  color: var(--wt-muted);
}

.wt-day__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
}
.wt-day__datebox {
  display: flex;
  align-items: center;
  gap: 10px;
}
.wt-day__num {
  font-size: 40px;
  font-weight: 900;
  line-height: 0.95;
  letter-spacing: -1.5px;
  color: var(--wt-ink);
}
.wt-day__meta {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
}
.wt-day__weekday {
  font-weight: 900;
  font-size: 15px;
  color: var(--wt-blue-deep);
  letter-spacing: 0.2px;
  text-transform: uppercase;
}
.wt-day__month {
  color: var(--wt-muted);
  font-size: 12.5px;
  font-weight: 700;
}
.wt-day__badges {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
}
.wt-day__badge {
  background: var(--wt-yellow);
  color: var(--wt-ink);
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 3px 9px;
  border-radius: 999px;
}
.wt-day__avail {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(46, 160, 90, 0.12);
  color: #1d7a43;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 999px;
  white-space: nowrap;
}
.wt-day__avail::before {
  content: "";
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--wt-green);
}
.wt-day__avail--full {
  background: rgba(200, 60, 50, 0.1);
  color: var(--wt-red);
}
.wt-day__avail--full::before {
  background: var(--wt-red);
}

.wt-day__slots {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  align-content: start;
}
.wt-slot {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 10px 6px 8px;
  border: 1.5px solid var(--wt-blue-line);
  border-radius: 10px;
  background: var(--wt-blue-soft);
  color: var(--wt-ink);
  cursor: pointer;
  font-family: inherit;
  transition: all 0.14s ease;
}
.wt-slot__time {
  font-weight: 900;
  font-size: 16.5px;
  letter-spacing: -0.3px;
}
.wt-slot__state {
  font-size: 9.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  opacity: 0.65;
}
.wt-slot--free:hover {
  background: var(--wt-blue);
  border-color: var(--wt-blue);
  color: #fff;
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(44, 91, 158, 0.32);
}
.wt-slot--free:hover .wt-slot__state {
  opacity: 1;
}
.wt-slot--booked {
  background: #faf1f0;
  border-color: #f0d5d2;
  color: #b98c86;
  cursor: not-allowed;
}
.wt-slot--booked .wt-slot__time {
  text-decoration: line-through;
  text-decoration-thickness: 2px;
  text-decoration-color: rgba(200, 60, 50, 0.5);
}
.wt-slot--booked .wt-slot__state {
  color: var(--wt-red);
  opacity: 0.75;
}
.wt-day__noslots {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: var(--wt-muted);
  text-align: center;
  padding: 12px 4px;
}
.wt-day__noslots span {
  font-size: 26px;
  line-height: 1;
  opacity: 0.5;
}
.wt-day__noslots small {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-top: 6px;
  font-weight: 700;
}

/* ============== EMPTY/LOADING ============== */
.wt-empty-banner {
  background: #fff;
  border: 1.5px solid var(--wt-blue-line);
  border-left: 4px solid var(--wt-blue);
  padding: 16px 20px;
  margin-bottom: 20px;
  color: var(--wt-ink);
  font-size: 14.5px;
}
.wt-loading-inline {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  margin-bottom: 16px;
  background: #fff;
  border: 1.5px dashed var(--wt-blue-line);
  color: var(--wt-text);
  font-size: 13px;
  font-weight: 700;
  width: fit-content;
}
.wt-loading__spinner {
  width: 18px;
  height: 18px;
  border: 2.5px solid var(--wt-blue-line);
  border-top-color: var(--wt-blue);
  border-radius: 50%;
  animation: wt-spin 0.7s linear infinite;
}
@keyframes wt-spin {
  to { transform: rotate(360deg); }
}
.wt-grid--loading {
  opacity: 0.6;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

/* ============== MODAL ============== */
.wt-modal {
  position: fixed;
  inset: 0;
  background: rgba(10, 25, 50, 0.6);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 80;
  padding: 64px 16px;
  overflow-y: auto;
}
.wt-modal__inner {
  background: #fff;
  width: 100%;
  max-width: 520px;
  position: relative;
  border: 1px solid var(--wt-blue-line);
  box-shadow: 0 24px 60px rgba(10, 25, 50, 0.32);
  padding: 32px 32px 28px;
}
.wt-modal__inner::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--wt-yellow);
}
.wt-modal__h {
  font-size: 28px;
  font-weight: 900;
  letter-spacing: -0.5px;
  color: var(--wt-ink);
  margin: 8px 0 12px;
  line-height: 1.1;
}
.wt-modal__close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 36px;
  height: 36px;
  background: transparent;
  border: 1px solid var(--wt-blue-line);
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
  color: var(--wt-muted);
  cursor: pointer;
}
.wt-modal__close:hover {
  background: var(--wt-blue);
  border-color: var(--wt-blue);
  color: #fff;
}
.wt-modal__when {
  color: var(--wt-text);
  font-size: 15px;
  margin: 0 0 22px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--wt-blue-line);
}
.wt-modal__when strong { color: var(--wt-blue); }
.wt-modal__dur {
  color: var(--wt-muted);
  font-size: 13px;
  margin-left: 6px;
}

/* ============== FORM ============== */
.wt-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.wt-form__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
@media (max-width: 480px) {
  .wt-form__row { grid-template-columns: 1fr; }
}
.wt-form__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: var(--wt-ink);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.wt-form__field input,
.wt-form__field textarea {
  font-family: inherit;
  font-size: 15px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  padding: 11px 13px;
  border: 1.5px solid var(--wt-blue-line);
  background: #fff;
  color: var(--wt-ink);
  transition: border 0.15s ease, box-shadow 0.15s ease;
}
.wt-form__field select {
  font-family: inherit;
  font-size: 15px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  padding: 11px 13px;
  border: 1.5px solid var(--wt-blue-line);
  background: #fff;
  color: var(--wt-ink);
}
.wt-form__field input:focus,
.wt-form__field textarea:focus,
.wt-form__field select:focus {
  outline: none;
  border-color: var(--wt-blue);
  box-shadow: 0 0 0 3px rgba(44, 91, 158, 0.15);
}

/* ---- Versicherungsstatus ---- */
.wt-form__fieldset {
  border: 1.5px solid var(--wt-blue-line);
  border-radius: 10px;
  padding: 12px 14px;
  margin: 0;
}
.wt-form__fieldset legend {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--wt-ink);
  padding: 0 6px;
}
.wt-form__radio {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 2px;
  font-size: 14.5px;
  color: var(--wt-text);
  cursor: pointer;
}
.wt-form__radio input {
  width: 17px;
  height: 17px;
  accent-color: var(--wt-blue);
  cursor: pointer;
  flex-shrink: 0;
}
.wt-form__error {
  background: #fdecea;
  color: var(--wt-red);
  padding: 11px 14px;
  font-size: 14px;
  font-weight: 600;
  border-left: 4px solid var(--wt-red);
}

/* ===== Hinweis Versicherungsprinzip ===== */
.wt-prinzip {
  background: #fff8e6;
  border-left: 4px solid #e0a100;
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 13.5px;
  line-height: 1.5;
}
.wt-prinzip p {
  margin: 0 0 6px;
}
.wt-prinzip p:last-child {
  margin-bottom: 0;
}
.wt-prinzip__h {
  font-weight: 700;
  font-size: 14.5px;
}
.wt-prinzip__bsp {
  font-style: italic;
}

/* ===== Erstinformation ===== */
.wt-erstinfo {
  background: var(--wt-blue-soft);
  border: 1.5px solid var(--wt-blue-line);
  border-radius: 10px;
  padding: 12px 14px;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.wt-erstinfo--error {
  background: #fdf2f1;
  border-color: var(--wt-red);
}
.wt-erstinfo__check {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  text-transform: none;
  letter-spacing: 0;
}
.wt-erstinfo__check input {
  margin-top: 3px;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  accent-color: var(--wt-green);
  cursor: pointer;
}
.wt-erstinfo__text {
  font-size: 13px;
  font-weight: 400;
  line-height: 1.55;
  color: var(--wt-text);
}
.wt-erstinfo__text strong {
  display: block;
  font-size: 13.5px;
  color: var(--wt-ink);
  margin-bottom: 2px;
}
.wt-erstinfo__ja {
  color: var(--wt-green);
  font-weight: 900;
}
.wt-erstinfo__link {
  color: var(--wt-blue);
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}
.wt-erstinfo__link:hover {
  color: var(--wt-blue-deep);
}
.wt-erstinfo__hint {
  margin: 8px 0 0;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--wt-red);
  text-transform: none;
  letter-spacing: 0;
}
.wt-form__hint {
  font-size: 11.5px;
  color: var(--wt-muted);
  text-align: center;
  margin: 0;
  line-height: 1.5;
}
.wt-form__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 22px;
  background: var(--wt-blue);
  color: #fff !important;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  text-decoration: none !important;
  border: 2px solid var(--wt-blue);
  border-left: 5px solid var(--wt-yellow);
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s ease;
}
.wt-form__cta:hover:not(:disabled) {
  background: var(--wt-blue-deep);
  border-color: var(--wt-blue-deep);
  border-left-color: var(--wt-yellow);
}
.wt-form__cta:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.wt-form__cta--ghost {
  background: transparent;
  color: var(--wt-blue) !important;
  border: 2px solid var(--wt-blue-line);
  border-left: 2px solid var(--wt-blue-line);
}
.wt-form__cta--ghost:hover:not(:disabled) {
  background: var(--wt-blue-soft);
  border-color: var(--wt-blue);
  border-left-color: var(--wt-blue);
  color: var(--wt-blue-deep) !important;
}
.wt-form__success {
  text-align: center;
  padding: 16px 8px 4px;
}
.wt-form__success h3 {
  font-size: 28px;
  font-weight: 900;
  color: var(--wt-ink);
  margin: 0 0 8px;
  letter-spacing: -0.5px;
}
.wt-form__success p {
  color: var(--wt-text);
  margin: 4px 0;
}
.wt-form__success .wt-form__cta {
  margin-top: 18px;
}
</style>
